import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useRef,
  useImperativeHandle,
} from 'react'

import { fileSize } from '@bettermode/common/filesize'
import { useNetwork, useFileUploader } from '@tribeplatform/react-sdk/hooks'
import { ButtonSize } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useI18n } from '../../i18n/index.js'
import { useComposer } from '../hooks/useComposer.js'

import { ComposerIconButton } from './ComposerIconButton.js'

interface Props {
  size?: ButtonSize
}
export type AttachmentPickerButtonRef = {
  open: () => void
}

export const AttachmentPickerButton = forwardRef<
  AttachmentPickerButtonRef,
  Props
>(({ size }, ref) => {
  const { onFileUpload } = useComposer()
  const { data: network } = useNetwork()
  const { fileTypesLimit } = network?.settings || {}
  const hiddenFileInput = useRef<HTMLInputElement>()
  const { getLimitExceedingFiles } = useFileUploader()
  const { $t } = useI18n()

  const onAttachmentInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return
      }

      const files = Array.from(event.target.files)

      const { sizeLimitExceededFiles, limit } = getLimitExceedingFiles(files)

      if (sizeLimitExceededFiles.length > 0) {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t(
            {
              id: 'Generics.FileSizeLimit',
              defaultMessage: 'The file size limit is {size_limit}.',
            },
            { size_limit: fileSize(limit) },
          ),
          status: 'error',
        })
        return
      }

      onFileUpload?.(files, { asAttachment: true })

      event.target.value = ''
    },
    [onFileUpload, $t, getLimitExceedingFiles],
  )

  const open = () => {
    hiddenFileInput.current.click()
  }

  useImperativeHandle(ref, () => ({
    open,
  }))

  if (typeof onFileUpload !== 'function') {
    return null
  }

  return (
    <div>
      <ComposerIconButton
        icon={<SvgIcon name="attachment" />}
        aria-label="Attachment"
        onClick={open}
        size={size}
      />
      <input
        onChange={onAttachmentInputChange}
        ref={hiddenFileInput}
        accept={fileTypesLimit?.join(',') || undefined}
        type="file"
        className="hidden"
        multiple
      />
    </div>
  )
})
